import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { UpdoxDirectMessage, ComposeDraft } from '../updox-direct-message';
import { UpdoxMessagesService } from '../updox-messages.service';
import { AlertService } from '../../../../_alert';
import { GlobalErrorHandlerService } from '../../../../services/global-error.service';

@Component({
  selector: 'app-updox-message-detail',
  templateUrl: './message-detail.component.html',
})
export class UpdoxMessageDetailComponent implements OnChanges, OnDestroy {
  @Input() message: UpdoxDirectMessage;
  @Output() reply = new EventEmitter<ComposeDraft>();
  @Output() editDraft = new EventEmitter<UpdoxDirectMessage>();
  @Output() deleted = new EventEmitter<UpdoxDirectMessage>();

  isDeleting = false;
  showDeleteConfirm = false;
  isMarkingUnread = false;
  showAttachments = false;
  isLoadingBody = false;
  fullMessage: UpdoxDirectMessage | null = null;

  // Patient link bar
  showLinkSearch = false;
  isLinking = false;
  linkPatientQuery = '';
  linkPatientResults: any[] = [];
  linkSearchLoading = false;
  showLinkDropdown = false;
  linkedPatientDetail: any = null; // { id, name, complete_detail } from link response — richer than patient_name
  private linkSearchTimer: any;
  private linkSearchVersion = 0;
  private linkMutatedLocally = false; // guards a slow getMessage response from clobbering a fresh link/unlink

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.message) {
      this.showAttachments   = false;
      this.showDeleteConfirm = false;
      this.fullMessage       = null;
      this.resetLinkState();

      if (this.message) {
        this.isLoadingBody = true;
        // Guard against a stale response landing after the user switched to
        // another message — it must not populate the newer message's view.
        const requestedId = this.message.id;
        this.messagesService.getMessage(requestedId)
          .then(json => {
            if (this.message?.id !== requestedId) { return; }
            this.isLoadingBody = false;
            this.fullMessage = new UpdoxDirectMessage().load_from_json(json);
            // The list payload may predate a link made elsewhere — trust the fresh
            // copy, unless the user just linked/unlinked while this request was in flight.
            if (!this.linkMutatedLocally) {
              this.message.patient_id = this.fullMessage.patient_id;
              this.message.patient_name = this.fullMessage.patient_name;
            }
          })
          .catch(() => {
            if (this.message?.id !== requestedId) { return; }
            this.isLoadingBody = false;
            this.fullMessage = this.message;
          });
      }
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.linkSearchTimer);
  }

  private resetLinkState(): void {
    clearTimeout(this.linkSearchTimer);
    this.showLinkSearch = false;
    this.isLinking = false;
    this.linkPatientQuery = '';
    this.linkPatientResults = [];
    this.linkSearchLoading = false;
    this.showLinkDropdown = false;
    this.linkedPatientDetail = null;
    this.linkMutatedLocally = false;
  }

  get isDraft(): boolean {
    return this.message?.folder === 'draft';
  }

  get isInbound(): boolean {
    return this.message?.direction === 'inbound';
  }

  get deleteConfirmText(): string {
    if (this.message?.folder === 'deleted') {
      return 'This will permanently remove the message and cannot be undone.';
    }
    if (this.message?.folder === 'draft') {
      return 'This draft will be permanently deleted.';
    }
    return 'This message will be moved to Trash. You can find it in the Deleted folder.';
  }

  constructor(
    private messagesService: UpdoxMessagesService,
    private alertService: AlertService,
    private globalErrorHandler: GlobalErrorHandlerService,
  ) {}

  replyToMessage(): void {
    const original = this.fullMessage || this.message;
    const quotedDate = original.created_at
      ? new Date(original.created_at).toLocaleString()
      : '';
    const quotedBody = [
      '',
      '',
      `--- Original Message ---`,
      `From: ${original.from_address}`,
      quotedDate ? `Date: ${quotedDate}` : '',
      `Subject: ${original.subject}`,
      '',
      original.body_text || '',
    ].filter((line, i) => i < 4 || line !== '').join('\n');

    const draft: ComposeDraft = {
      localId:             Date.now(),
      to_address:          original.from_address,
      subject:             original.subject.startsWith('Re:')
                             ? original.subject
                             : `Re: ${original.subject}`,
      body_text:           quotedBody,
      reply_to_message_id: original.id,
      patient_id:          original.patient_id || undefined,
      minimized:           false,
      replyTo:             original,
    };
    this.reply.emit(draft);
  }

  markUnread(): void {
    if (this.isMarkingUnread) { return; }
    this.isMarkingUnread = true;
    this.messagesService.markRead(this.message.id, false)
      .then(() => {
        this.isMarkingUnread = false;
        this.message.read = false;
      })
      .catch(() => {
        this.isMarkingUnread = false;
        this.globalErrorHandler.error = 'Failed to mark message as unread.';
      });
  }

  confirmDelete(): void {
    this.showDeleteConfirm = true;
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
  }

  // --- Patient link bar ---

  get patientDisplayName(): string {
    if (this.message?.patient_name) { return this.message.patient_name; }
    return this.message?.patient_id ? `Patient #${this.message.patient_id}` : '';
  }

  openLinkSearch(): void {
    this.showLinkSearch = true;
    this.linkPatientQuery = '';
    this.linkPatientResults = [];
    this.showLinkDropdown = false;
    this.linkSearchLoading = false;
  }

  cancelLinkSearch(): void {
    clearTimeout(this.linkSearchTimer);
    this.showLinkSearch = false;
    this.linkPatientQuery = '';
    this.linkPatientResults = [];
    this.showLinkDropdown = false;
    this.linkSearchLoading = false;
  }

  onLinkQueryChange(value: string): void {
    this.linkPatientQuery = value;
    clearTimeout(this.linkSearchTimer);
    this.linkPatientResults = [];
    this.showLinkDropdown = false;
    if (!value || value.trim().length < 2) {
      this.linkSearchLoading = false;
      return;
    }
    this.linkSearchLoading = true;
    this.linkSearchTimer = setTimeout(() => this.runLinkSearch(value.trim()), 300);
  }

  private runLinkSearch(query: string): void {
    const version = ++this.linkSearchVersion;
    this.messagesService.searchPatients(query, this.message.practice_id)
      .then(results => {
        if (version !== this.linkSearchVersion) { return; }
        this.linkSearchLoading = false;
        this.linkPatientResults = results || [];
        this.showLinkDropdown = this.linkPatientResults.length > 0;
      })
      .catch(() => {
        if (version !== this.linkSearchVersion) { return; }
        this.linkSearchLoading = false;
      });
  }

  selectLinkPatient(p: any): void {
    if (this.isLinking || !p?.id) { return; }
    this.isLinking = true;
    this.showLinkDropdown = false;
    // Pin the target: if the user switches messages while the PUT is in flight,
    // the persisted link still belongs to this message — update its list row,
    // but leave the newly selected message's view alone.
    const msg = this.message;
    this.messagesService.linkPatient(msg.id, p.id)
      .then(resp => {
        // msg is shared with the list row — mutating it updates the chip too.
        msg.patient_id = resp?.patient_id || p.id;
        msg.patient_name = resp?.patient?.name || p.name || '';
        if (this.message === msg) {
          this.isLinking = false;
          this.linkMutatedLocally = true;
          this.linkedPatientDetail = resp?.patient || p;
          this.cancelLinkSearch();
        }
        this.alertService.success('Patient linked to message.', this.alertService.options);
      })
      .catch(resp => {
        if (this.message === msg) { this.isLinking = false; }
        this.globalErrorHandler.error = resp?.error?.error || 'Failed to link patient. Please try again.';
      });
  }

  unlinkPatient(): void {
    if (this.isLinking) { return; }
    this.isLinking = true;
    const msg = this.message;
    this.messagesService.linkPatient(msg.id, null)
      .then(() => {
        msg.patient_id = null;
        msg.patient_name = '';
        if (this.message === msg) {
          this.isLinking = false;
          this.linkMutatedLocally = true;
          this.linkedPatientDetail = null;
        }
        this.alertService.success('Patient unlinked from message.', this.alertService.options);
      })
      .catch(resp => {
        if (this.message === msg) { this.isLinking = false; }
        this.globalErrorHandler.error = resp?.error?.error || 'Failed to unlink patient. Please try again.';
      });
  }

  closeLinkDropdownDelayed(): void {
    setTimeout(() => { this.showLinkDropdown = false; }, 150);
  }

  patientInitialFor(p: any): string {
    return p?.name?.charAt(0)?.toUpperCase() || '?';
  }

  patientDemographics(p: any): string {
    if (!p?.complete_detail) { return ''; }
    const dashIdx = p.complete_detail.lastIndexOf(' - ');
    const head = dashIdx >= 0 ? p.complete_detail.substring(0, dashIdx) : p.complete_detail;
    const commaIdx = head.indexOf(', ');
    return commaIdx >= 0 ? head.substring(commaIdx + 2) : '';
  }

  deleteMessage(): void {
    this.isDeleting = true;
    this.showDeleteConfirm = false;
    this.messagesService.deleteMessage(this.message.id)
      .then((resp: any) => {
        this.isDeleting = false;
        const toast = resp?.permanently_deleted
          ? 'Message permanently deleted.'
          : 'Message moved to Trash.';
        this.alertService.success(toast, this.alertService.options);
        this.deleted.emit(this.message);
      })
      .catch(() => {
        this.isDeleting = false;
        this.globalErrorHandler.error = 'Failed to delete message. Please try again.';
      });
  }
}
